import React from 'react';
import { 
  TrendingUp, 
  DollarSign, 
  Target, 
  Trophy,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Users,
  Clock,
  Star
} from 'lucide-react';
import TradingChart from '../components/TradingChart';
import RecentTrades from '../components/RecentTrades';
import ActiveCompetitions from '../components/ActiveCompetitions';

const Dashboard: React.FC = () => {
  const stats = [
    {
      title: 'Portfolio Value',
      value: '$12,450.00',
      change: '+$234.50',
      changePercent: '+1.92%',
      icon: DollarSign,
      trend: 'up'
    },
    {
      title: 'Total Profit',
      value: '$3,450.00',
      change: '+$156.20',
      changePercent: '+4.74%',
      icon: TrendingUp,
      trend: 'up'
    },
    {
      title: 'Active Trades',
      value: '12',
      change: '+3',
      changePercent: '+33.3%',
      icon: Activity,
      trend: 'up'
    },
    {
      title: 'Win Rate',
      value: '78.5%',
      change: '+2.1%',
      changePercent: '+2.75%',
      icon: Target,
      trend: 'up'
    },
  ];

  const quickActions = [
    { title: 'Quick Buy', subtitle: 'Market order', icon: TrendingUp, color: 'bg-green-500' },
    { title: 'Quick Sell', subtitle: 'Market order', icon: ArrowDownRight, color: 'bg-red-500' },
    { title: 'AI Trading', subtitle: 'Auto mode', icon: Star, color: 'bg-win-dark-accent' },
    { title: 'Join Competition', subtitle: '3 active', icon: Trophy, color: 'bg-win-dark-gold' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome Section */}
      <div className="win-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-win-dark-text mb-2">
              Welcome back, John! 👋
            </h1>
            <p className="text-win-dark-text-muted">
              Here's what's happening with your gold trading today.
            </p>
          </div>
          <div className="hidden lg:flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-win-dark-gold">#{12}</p>
              <p className="text-sm text-win-dark-text-muted">Global Rank</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-win-dark-text">2,450</p>
              <p className="text-sm text-win-dark-text-muted">Points</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="win-stat-card group">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-3xl ${
                stat.trend === 'up' ? 'bg-green-500/20' : 'bg-red-500/20'
              }`}>
                <stat.icon className={`w-6 h-6 ${
                  stat.trend === 'up' ? 'text-green-400' : 'text-red-400'
                }`} />
              </div>
              <div className={`flex items-center gap-1 text-sm font-medium ${
                stat.trend === 'up' ? 'text-green-400' : 'text-red-400'
              }`}>
                {stat.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                {stat.changePercent}
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-win-dark-text mb-1">{stat.value}</p>
              <p className="text-sm text-win-dark-text-muted">{stat.title}</p>
              <p className={`text-sm font-medium ${
                stat.trend === 'up' ? 'text-green-400' : 'text-red-400'
              }`}>
                {stat.change}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="win-card p-6">
        <h2 className="text-xl font-bold text-win-dark-text mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <button 
              key={index}
              className="flex items-center gap-4 p-4 bg-win-dark-surface-light rounded-3xl border border-win-dark-border hover:border-win-dark-accent/50 transition-all duration-200 hover:scale-105 group"
            >
              <div className={`p-3 rounded-2xl ${action.color}`}>
                <action.icon className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="font-medium text-win-dark-text group-hover:text-win-dark-accent transition-colors">
                  {action.title}
                </p>
                <p className="text-sm text-win-dark-text-muted">{action.subtitle}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Trading Chart */}
        <div className="xl:col-span-2">
          <TradingChart />
        </div>

        {/* Active Competitions */}
        <div>
          <ActiveCompetitions />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentTrades />
        
        {/* AI Trading Status */}
        <div className="win-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-win-dark-text">AI Trading Status</h2>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-green-400 font-medium">Active</span>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-win-dark-surface-light rounded-3xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-win-dark-accent/20 rounded-2xl">
                  <Star className="w-5 h-5 text-win-dark-accent" />
                </div>
                <div>
                  <p className="font-medium text-win-dark-text">Conservative Strategy</p>
                  <p className="text-sm text-win-dark-text-muted">Risk Level: Low</p>
                </div>
              </div>
              <button className="win-button-secondary">Configure</button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-win-dark-surface-light rounded-3xl">
                <p className="text-2xl font-bold text-green-400">15</p>
                <p className="text-sm text-win-dark-text-muted">Successful Trades</p>
              </div>
              <div className="text-center p-4 bg-win-dark-surface-light rounded-3xl">
                <p className="text-2xl font-bold text-win-dark-text">$1,245</p>
                <p className="text-sm text-win-dark-text-muted">AI Profit Today</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;